import { supabase } from './supabaseClient';
import { localizedPath } from '../i18n/locale';
import { getCurrentLocale } from '../i18n/format';

export interface ProjectInvite {
  id: string;
  project_id: string;
  email: string;
  role: string;
  invited_by: string | null;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface ProjectInviteInfo {
  invite_id: string;
  project_name: string;
  inviter_name: string;
  email: string;
  role: string;
  expires_at: string;
  is_expired: boolean;
  is_accepted: boolean;
}

export const projectInviteService = {
  /**
   * Criar convite para email (cadastrado ou não)
   * Retorna token para construir link de convite
   */
  async invite(projectId: string, email: string, role: string = 'editor'): Promise<{ token: string; inviteId: string }> {
    const { data, error } = await supabase.rpc('invite_to_project', {
      p_project_id: projectId,
      p_email: email,
      p_role: role
    });

    if (error) throw error;
    const row = (data as Array<{ token: string; invite_id: string }>)?.[0];
    if (!row) throw new Error('Erro ao criar convite');
    return { token: row.token, inviteId: row.invite_id };
  },

  /**
   * Convite em lote (botão "Convidar (N)" da tela Equipe & Alocação).
   * Loop sequencial sobre o RPC unitário, que já é idempotente (renova o
   * expires_at de convite pendente). Um e-mail que falha não derruba os outros.
   */
  async inviteMany(
    projectId: string,
    people: { email: string; name?: string }[],
    role: string = 'editor'
  ): Promise<{ email: string; ok: boolean; emailSent: boolean; error?: string }[]> {
    const results: { email: string; ok: boolean; emailSent: boolean; error?: string }[] = [];
    for (const person of people) {
      const email = person.email?.trim().toLowerCase();
      if (!email) {
        results.push({ email: person.email || '', ok: false, emailSent: false, error: 'no_email' });
        continue;
      }
      try {
        const { inviteId } = await this.invite(projectId, email, role);
        const sent = await this.sendInviteEmail(inviteId);
        results.push({ email, ok: true, emailSent: !!sent?.success });
      } catch (err) {
        results.push({ email, ok: false, emailSent: false, error: err instanceof Error ? err.message : String(err) });
      }
    }
    return results;
  },

  /**
   * Aceitar convite (usuário autenticado)
   */
  async accept(token: string): Promise<{ projectId: string; role: string; projectName: string; alreadyMember: boolean }> {
    const { data, error } = await supabase.rpc('accept_project_invite', {
      p_token: token
    });

    if (error) throw error;
    const row = (data as Array<{ project_id: string; role: string; project_name: string; already_member: boolean }>)?.[0];
    if (!row) throw new Error('Erro ao aceitar convite');
    return {
      projectId: row.project_id,
      role: row.role,
      projectName: row.project_name,
      alreadyMember: row.already_member
    };
  },

  /**
   * Buscar info do convite por token (sem precisar de auth)
   */
  async getByToken(token: string): Promise<ProjectInviteInfo | null> {
    const { data, error } = await supabase.rpc('get_project_invite_by_token', {
      p_token: token
    });

    if (error) throw error;
    const row = (data as ProjectInviteInfo[])?.[0];
    return row || null;
  },

  /**
   * Listar convites pendentes de um projeto
   */
  async getPendingByProject(projectId: string): Promise<ProjectInvite[]> {
    const { data, error } = await supabase
      .from('project_invites')
      .select('*')
      .eq('project_id', projectId)
      .is('accepted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ProjectInvite[];
  },

  /**
   * Enviar email de convite via Edge Function.
   * Detecta tanto erro HTTP quanto skipped/error no body (Edge Function pode
   * retornar 200 com { skipped: true } se não houver provedor configurado).
   */
  async sendInviteEmail(inviteId: string): Promise<{ success: boolean; reason?: string; error?: string }> {
    const { data, error } = await supabase.functions.invoke('send-project-invite-email', {
      body: { invite_id: inviteId }
    });
    if (error) {
      console.error('[projectInviteService] Edge Function falhou:', error);
      return { success: false, error: String(error) };
    }
    if (data?.skipped) {
      console.warn('[projectInviteService] Envio pulado:', data.reason);
      return { success: false, reason: data.reason };
    }
    if (data?.error) {
      console.error('[projectInviteService] Erro retornado pelo provedor:', data.error);
      return { success: false, error: data.error };
    }
    return { success: true };
  },

  /**
   * Construir URL do convite.
   *
   * O link leva o prefixo de idioma de quem convida. Quem recebe nunca usou o
   * sistema, então não tem preferência gravada em localStorage, e sem o
   * prefixo `resolveLocale` cai no português: um convite mandado por um time
   * que trabalha em inglês abriria em português na primeira tela que a pessoa
   * vê do produto. O idioma do convidador é o melhor palpite disponível, e não
   * custa coluna nova nem migration.
   */
  buildInviteUrl(token: string): string {
    const base = window.location.origin;
    return `${base}${localizedPath(`/project-invite/${token}`, getCurrentLocale())}`;
  }
};
